import {
  type PropsWithChildren,
  useCallback,
  useRef,
  useState,
  useLayoutEffect,
} from 'react';
import { easings, animated } from '@react-spring/web';
import { useMentiSpring } from '@mentimeter/motion/spring';
import type { Word } from './WordCloud';

export const WordCloudResizer = ({
  children,
  height,
  width,
  words,
  disableAnimations,
}: PropsWithChildren<{
  height: number;
  width: number;
  words: Word[];
  disableAnimations: boolean;
}>) => {
  const wordCloudRef = useRef<SVGSVGElement>(null);
  const [scale, setScale] = useState<number>(0.1);

  const calculateScale = useCallback(() => {
    if (wordCloudRef.current && width > 0 && height > 0) {
      const bbox = wordCloudRef.current.getBBox();
      if (bbox.width > 0 && bbox.height > 0) {
        const scale = Math.min(height / bbox.height, width / bbox.width);
        const roundedScale = Math.floor(scale * 10) / 10;
        setScale(roundedScale);
      }
    }
  }, [height, width]);

  useLayoutEffect(() => {
    if (words.length > 0) {
      calculateScale();
    }
  }, [words, calculateScale]);

  const [{ transform }] = useMentiSpring({
    from: { transform: `scale(${scale})` },
    to: { transform: `scale(${scale})` },
    config: {
      easing: easings.easeOutBounce,
    },
    immediate: disableAnimations,
  });

  return (
    <g transform={`translate(${width / 2},${height / 2})`}>
      <animated.g
        ref={wordCloudRef}
        style={{
          transform,
          transformBox: 'fill-box',
          transformOrigin: 'center',
        }}
      >
        {children}
      </animated.g>
    </g>
  );
};
